import type { AlgorithmChallenge } from '@code-arcade/engine'

export const selectionSort: AlgorithmChallenge = {
  id: 'selection-sort',
  title: 'Selection Sort',
  level: 1,
  chapter: 'chapter-1',
  mode: 'race-it',
  description: 'Scan for the minimum. Place it. Repeat. O(n²) comparisons — but only O(n) swaps.',
  realWorld: {
    scenario: 'The admin table now logs every row move to an audit trail. Each swap writes to disk.',
    consequence: 'At 500 rows, Bubble Sort triggers up to 124,750 writes. Selection Sort: at most 499.',
    products: [],
  },
  visualization: {
    type: 'bar-array',
    defaultSize: 20,
    allowCustomInput: false,
  },
  interrupts: [
    {
      atStep: 20,
      character: 'chad-mercer',
      mood: 'idle',
      message: "Fewer swaps than bubble. Still O(n²) though. Classic junior move — optimising the wrong thing.",
    },
  ],
  complexity: { time: 'O(n²)', space: 'O(1)' },
  xpReward: { oneStar: 75, twoStar: 150, threeStar: 300 },

  keyTakeaway:
    'Selection sort makes the fewest writes of any simple sort — at most n−1, regardless of input. ' +
    'That matters because write cost dominates in many real systems: every database upsert, every S3 write, ' +
    'every audit-log entry has a price that dwarfs a comparison. Selection sort trains the habit of asking ' +
    '"what is the expensive operation in this loop, and can I minimize it?" Database query planners think this ' +
    'way on every execution plan. So do engineers designing batch pipelines that call an external API once per ' +
    'item instead of once per comparison.',

  whenToUse: [
    'Storage where writes are expensive — EEPROM, flash, or audit-logged systems',
    'Any system where writes are more expensive than reads — database upserts, S3 uploads, audit trails, flash memory writes',
    'Batch jobs that minimize expensive operations: select the best candidate first, commit once — not once per comparison',
    'Physically sorting objects (robotic arms, card sorters) where movement costs more than comparison',
    'Teaching the concept of "find the minimum" as a building block for more advanced algorithms',
  ],

  whenNotToUse: [
    'General-purpose sorting — n² comparisons still kills you at scale',
    'Nearly-sorted data — insertion sort dominates here with far fewer comparisons',
    'Any dataset over a few hundred elements in latency-sensitive code',
  ],

  pseudocode: `procedure selectionSort(A)
  n ← length(A)
  for i from 0 to n - 2
    minIdx ← i
    for j from i + 1 to n - 1
      if A[j] < A[minIdx]
        minIdx ← j
    if minIdx ≠ i
      swap A[i] and A[minIdx]
  return A`,

  codeSnippets: [
    {
      language: 'csharp',
      label: 'C#',
      code: `public static void SelectionSort(int[] arr) {
    int n = arr.Length;
    for (int i = 0; i < n - 1; i++) {
        int minIdx = i;
        for (int j = i + 1; j < n; j++) {
            if (arr[j] < arr[minIdx])
                minIdx = j;
        }
        if (minIdx != i)
            (arr[i], arr[minIdx]) = (arr[minIdx], arr[i]);
    }
}

// Array.sort() makes more writes per element, but far fewer total — a different trade-off you now understand:
Array.Sort(arr);               // introspective sort O(n log n)
list.Sort();                   // List<T> — same internals`,
    },
    {
      language: 'python',
      label: 'Python',
      code: `def selection_sort(arr: list[int]) -> list[int]:
    n = len(arr)
    for i in range(n - 1):
        min_idx = i
        for j in range(i + 1, n):
            if arr[j] < arr[min_idx]:
                min_idx = j
        if min_idx != i:
            arr[i], arr[min_idx] = arr[min_idx], arr[i]
    return arr


# Array.sort() makes more writes per element, but far fewer total — a different trade-off you now understand:
arr.sort()                # in-place  O(n log n) TimSort
sorted_arr = sorted(arr)  # new list  O(n log n) TimSort`,
    },
    {
      language: 'javascript',
      label: 'JavaScript',
      code: `function selectionSort(arr) {
  const n = arr.length;
  for (let i = 0; i < n - 1; i++) {
    let minIdx = i;
    for (let j = i + 1; j < n; j++) {
      if (arr[j] < arr[minIdx]) minIdx = j;
    }
    if (minIdx !== i)
      [arr[i], arr[minIdx]] = [arr[minIdx], arr[i]];
  }
  return arr;
}

// Array.sort() makes more writes per element, but far fewer total — a different trade-off you now understand:
arr.sort((a, b) => a - b); // V8 TimSort, O(n log n)`,
    },
    {
      language: 'typescript',
      label: 'TypeScript',
      code: `function selectionSort(arr: number[]): number[] {
  const n = arr.length;
  for (let i = 0; i < n - 1; i++) {
    let minIdx = i;
    for (let j = i + 1; j < n; j++) {
      if (arr[j] < arr[minIdx]) minIdx = j;
    }
    if (minIdx !== i)
      [arr[i], arr[minIdx]] = [arr[minIdx], arr[i]];
  }
  return arr;
}

// Array.sort() makes more writes per element, but far fewer total — a different trade-off you now understand:
arr.sort((a, b) => a - b); // V8 TimSort, O(n log n)`,
    },
    {
      language: 'java',
      label: 'Java',
      code: `public static void selectionSort(int[] arr) {
    int n = arr.length;
    for (int i = 0; i < n - 1; i++) {
        int minIdx = i;
        for (int j = i + 1; j < n; j++) {
            if (arr[j] < arr[minIdx]) minIdx = j;
        }
        if (minIdx != i) {
            int temp = arr[i];
            arr[i] = arr[minIdx];
            arr[minIdx] = temp;
        }
    }
}

// Array.sort() makes more writes per element, but far fewer total — a different trade-off you now understand:
Arrays.sort(arr);                // dual-pivot quicksort O(n log n)
Arrays.sort(objArr, comparator); // TimSort for objects`,
    },
  ],

  sampleDataset: {
    label: 'Admin table row moves — Innotek audit log',
    preview: [1042, 2891, 3, 456, 1205, 788, 2034, 91, 1567, 342, 2750, 619],
    atScale: {
      n: 500,
      leftLabel: 'Bubble Sort',
      leftNote: 'up to 124,750 writes',
      rightLabel: 'Selection Sort',
      rightNote: '≤ 499 writes',
    },
  },
}
